import {DropdownMenu} from '@wandb/components';
import {isEqual} from 'lodash';
import React, {useCallback, useMemo} from 'react';

import {useDispatch} from '../../../state/hooks';
import * as SortActions from '../../../state/views/sort/actions';
import type {SortKey} from '../../../util/queryTypes';
import {useRunSetSort, useRunSetSortRef} from '../RunSelectorRunSetContext';
import type {WBTableColumn} from '../WBTable/types';
import {MenuItem} from './MenuItem';

export const HIDE_COLUMN_SORT_CONTROLS = [
  'Tags',
  'GPU Type',
  'GPU Count',
  'Issues',
];

/**
 * Returns the current sort direction for the given column, or an empty array
 * if the column is not part of the active sort.
 */
function useSelectedKey(
  column: WBTableColumn
): ['ascending'] | ['descending'] | [] {
  const sort = useRunSetSort();
  return useMemo(() => {
    const columnSortKey = sort.keys.find((sk: SortKey) =>
      isEqual(sk.key, column.key)
    );

    if (!columnSortKey) {
      return [];
    }
    if (columnSortKey.ascending) {
      return ['ascending'];
    }
    return ['descending'];
  }, [column, sort]);
}

/**
 * Returns a callback that upserts a sort key for the given column into the
 * run set's sort state. Intended for use with a single-selection dropdown:
 * the selected key must be either `'ascending'` or `'descending'`.
 */
function useUpsertSort(column: WBTableColumn) {
  const dispatch = useDispatch();
  const sortRef = useRunSetSortRef();

  return useCallback(
    (keys: 'all' | Set<React.Key>) => {
      if (keys === 'all') {
        return;
      }
      const [key] = keys;
      if (!key) {
        return;
      }
      const ascending = key === 'ascending';
      dispatch(SortActions.upsert(sortRef, {key: column.key, ascending}));
    },
    [dispatch, sortRef, column]
  );
}

type ColumnSortingOptionsProps = {
  column: WBTableColumn;
};

export function ColumnSortingOptions({column}: ColumnSortingOptionsProps) {
  const selectedKey = useSelectedKey(column);
  const handleSelectionChange = useUpsertSort(column);

  if (HIDE_COLUMN_SORT_CONTROLS.includes(column.displayName ?? '')) {
    return null;
  }

  const ascLabel =
    column.displayName === 'Created' ? 'Oldest first' : 'Ascending';
  const descLabel =
    column.displayName === 'Created' ? 'Newest first' : 'Descending';

  return (
    <DropdownMenu.Section
      selectedKeys={selectedKey}
      selectionMode="single"
      onSelectionChange={handleSelectionChange}>
      <DropdownMenu.Header>Sorting order</DropdownMenu.Header>
      <MenuItem icon="up" id="ascending">
        {ascLabel}
      </MenuItem>
      <MenuItem icon="down" id="descending">
        {descLabel}
      </MenuItem>
    </DropdownMenu.Section>
  );
}
